import { Component, OnInit, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AbstractControl, FormBuilder, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import {
  Destroyable,
  Rental,
  selectRentalById,
  untilDestroy,
  addSubUnitRentals,
  CreateRentalPayload,
  SaveForm,
  selectSubdomain,
  createRentalPayloadFromMainUnit,
  isSavingRental,
  integer,
  setRentalSubUnits,
  addSubUnitRentalsComplete,
  notAllSpacesValidator,
  notAllNumbersValidator,
  setRentalSubUnitsComplete,
} from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { Actions, ofType } from '@ngrx/effects'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '../../overlays/rental-overlay/rental-overlay.component'
import { RentalGroupSuccessDialogService } from '../rental-group-success-dialog/rental-group-success-dialog.service'
import { switchMap } from 'rxjs/operators'

@Component({
  selector: 'app-rental-to-multi-unit-dialog',
  templateUrl: './rental-to-multi-unit-dialog.component.html',
  styleUrls: ['./rental-to-multi-unit-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class RentalToMultiUnitDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    count: [2, [Validators.required, integer(), Validators.min(1), Validators.max(100)]],
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(140),
        notAllNumbersValidator,
        notAllSpacesValidator,
      ],
    ],
  })

  mainRental: Rental
  subDomain: string

  loading$ = this.store.pipe(select(isSavingRental))

  constructor(
    public dialogRef: MatDialogRef<RentalToMultiUnitDialogComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private actions: Actions,
    private successDialog: RentalGroupSuccessDialogService,
    @Inject(MAT_DIALOG_DATA) public data: { rentalId: string }
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.pipe(select(selectRentalById(this.data.rentalId)), untilDestroy(this)).subscribe((rental) => {
      this.mainRental = rental
      this.form.patchValue({ name: rental.name })
    })
    this.store.pipe(select(selectSubdomain), untilDestroy(this)).subscribe((subDomain) => {
      this.subDomain = subDomain
    })

    this.actions.pipe(ofType(addSubUnitRentalsComplete), untilDestroy(this)).subscribe(() => {
      this.store.dispatch(
        setRentalSubUnits({
          rentalId: this.mainRental.id,
          addedChildren: [this.mainRental.id],
          removedChildren: [],
        })
      )
    })
    this.actions
      .pipe(
        ofType(setRentalSubUnitsComplete),
        switchMap(() => this.showSuccessDialog()),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.close()
      })
  }

  getChildItemNames() {
    const { count, name } = this.form.getRawValue()
    return lodash.range(1, count + 1).map((index: number) => {
      return `${name || 'Subunit'} ${index.toString().padStart(2, '0')}`
    })
  }

  close() {
    this.dialogRef.close()
  }

  showSuccessDialog() {
    return this.successDialog
      .open({
        title: $localize`Multi-Unit Conversion Successful!`,
        message: $localize`Your rental has been successfully converted into a Multi-Unit setup.`,
        image: '/assets/images/rental-group/multi-unit-done-2.svg',
        steps: [
          $localize`Each subunit now has its own pricing, availability, and details, while the main unit remains unbookable.`,
          $localize`This setup is now reflected in AdvanceCM and other connected channels.`,
          $localize`You can manage subunits inside the rental details overlay under the "Multi-Unit Settings" tab.`,
        ],
        buttons: [
          {
            label: $localize`Multi-Unit Settings`,
            icon: 'cs-icon cs-icon-door-cog',
            classes: 'btn btn-secondary-info',
            action: () => {
              this.store.dispatch(
                OpenRentalOverlay({ rentalId: this.mainRental.id, activeTab: RentalDialogTab.MultiUnitSettings })
              )
            },
          },
          {
            label: $localize`Ok, got it`,
            icon: 'cs-icon cs-icon-check text-xs',
            classes: 'btn btn-secondary-info',
          },
        ],
      })
      .afterClosed()
  }

  @SaveForm()
  onSave(form: AbstractControl) {
    const data = this.form.getRawValue()

    const newItems = lodash.map(lodash.range(1, data.count + 1), (index: number): CreateRentalPayload => {
      const name = `${data.name || 'Subunit'} ${index.toString().padStart(2, '0')}`
      return {
        ...createRentalPayloadFromMainUnit(this.mainRental, name, this.subDomain),
        multi_unit_priority: index,
      }
    })
    this.store.dispatch(addSubUnitRentals({ items: newItems }))
  }
}
